import { createBookingQuestionnaireClient } from '../../../../lib/rpc/booking-questionnaire.client'
import { createContext, useCallback, useContext } from 'react'
import { useSelector } from '@xstate/react'
import type { ActorRefFrom, SnapshotFrom } from 'xstate'
import { schedulerMachine } from './machine'

// Types for the actor
type SchedulerActor = ActorRefFrom<typeof schedulerMachine>
type SchedulerSnapshot = SnapshotFrom<typeof schedulerMachine>

// Context for the scheduler actor
type QuestionnaireClient = ReturnType<typeof createBookingQuestionnaireClient>
const QuestionnaireContext = createContext<QuestionnaireClient | null>(null)

const SchedulerActorContext = createContext<SchedulerActor | null>(null)

/** Leaves the scheduler for the kiosk hub ("Zurück zum Start"). */
const SchedulerExitContext = createContext<(() => void) | null>(null)

/**
 * Provider component - wraps the flow and provides the actor to children
 */
export function SchedulerActorProvider({
  actor,
  children,
  questionnaireClient,
  onExit,
}: {
  actor: SchedulerActor
  questionnaireClient?: QuestionnaireClient
  /** Leaves the flow for the kiosk hub; defaults to resetting the machine. */
  onExit?: () => void
  children: React.ReactNode
}) {
  return <SchedulerActorContext.Provider value={actor}><SchedulerExitContext.Provider value={onExit ?? null}><QuestionnaireContext.Provider value={questionnaireClient ?? null}>{children}</QuestionnaireContext.Provider></SchedulerExitContext.Provider></SchedulerActorContext.Provider>
}

/**
 * Returns the action behind every "Zurück zum Start" in the scheduler: reset
 * the booking and leave the flow for the kiosk hub. Without a flow-level exit
 * (isolated stories/tests) it only resets the machine.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const exitToStart = useSchedulerExitToStart()
 * <Button onClick={exitToStart}>…</Button>
 * ```
 */
export function useSchedulerExitToStart(): () => void {
  const actor = useSchedulerActor()
  const onExit = useContext(SchedulerExitContext)
  return useCallback(function exitSchedulerToStart() {
    actor.send({ type: 'RESET' })
    onExit?.()
  }, [actor, onExit])
}

/**
 * Hook to get the scheduler actor for sending events
 */
export function useSchedulerActor(): SchedulerActor {
  const actor = useContext(SchedulerActorContext)
  if (!actor) {
    throw new Error('useSchedulerActor must be used within SchedulerActorProvider')
  }
  return actor
}

/**
 * Hook to send events to the scheduler machine
 */
export function useSchedulerSend() {
  const actor = useSchedulerActor()
  return actor.send
}

/**
 * Hook to select specific parts of the scheduler state
 * Only re-renders when the selected value changes
 */
export function useSchedulerSelector<T>(selector: (snapshot: SchedulerSnapshot) => T): T {
  const actor = useSchedulerActor()
  return useSelector(actor, selector)
}

/** Access the flow-owned transient session without storing it in platform settings.
 * @since 1.1.8
 * @example const client = useBookingQuestionnaireClient();
 */
export function useBookingQuestionnaireClient() {
  return useContext(QuestionnaireContext)
}
